import PersonIcon from '@mui/icons-material/Person'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import LanguageIcon from '@mui/icons-material/Language'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import {
  Box,
  Drawer,
  Avatar,
  Typography,
} from '@mui/material'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavigationEnum } from '../../../utils/constants'
import styles from './MobileDrawer.module.scss'
import Language from '../../shared-components/language'
import NotificationsNavbar from '../../shared-components/NotificationsNavbar'

interface IProps {
  items: Array<any>
  open: boolean
  handleDrawerToggle: Function
  isAuthenticated: boolean
  profile: any
  customNavigate: Function
  selectedButton: number
  setSelectedButton: Function
}

export const MobileDrawer = (props: IProps) => {
  const { 
    items, 
    open, 
    handleDrawerToggle, 
    isAuthenticated, 
    profile, 
    customNavigate, 
    selectedButton, 
    setSelectedButton 
  } = props
  
  const { t } = useTranslation()
  const [expandedItem, setExpandedItem] = useState<number | null>(null)

  const handleNavigate = (to: NavigationEnum) => {
    customNavigate(to)
    handleDrawerToggle()
  }

  const handleSubItemPressed = (value: string | number, to: NavigationEnum) => {
    if (to !== NavigationEnum.NOWHERE) {
      handleNavigate(to)
    }
  }

  const handleItemClick = (item: any) => {
    if (item.listOfItems && item.listOfItems.length > 0) {
      setExpandedItem(expandedItem === item.value ? null : item.value)
    } else if (item.to !== NavigationEnum.NOWHERE) {
      setSelectedButton(item.value)
      handleNavigate(item.to)
    }
  }

  const handleAccountClick = () => {
    setSelectedButton(6)
    if (isAuthenticated && profile) {
      handleNavigate(NavigationEnum.ACCOUNT)
    } else {
      handleNavigate(NavigationEnum.LOGIN)
    }
  }

  const getInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName[0]}${profile.lastName[0]}`
    }
    return profile?.firstName?.[0] || profile?.email?.[0] || 'U'
  }

  return (
    <Drawer
      variant='temporary'
      anchor='right'
      open={open}
      onClose={() => handleDrawerToggle()}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: '100%',
          maxWidth: '420px',
          background: 'transparent',
          border: 'none',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(12px)',
        }
      }}
    >
      <Box className={styles.drawer}>
        {/* Header */}
        <header className={styles.drawerHeader}>
          <div className={styles.headerTop}>
            {isAuthenticated && profile ? (
              <div className={styles.userCard}>
                <Avatar className={styles.userAvatar}>
                  {getInitials()}
                </Avatar>
                <div className={styles.userMeta}>
                  <span className={styles.userName}>
                    {profile?.firstName || profile?.email?.split('@')[0]}
                  </span>
                  <span className={styles.userBadge}>
                    {profile?.role?.name || 'Member'}
                  </span>
                </div>
                <div className={styles.userNotif}>
                  <NotificationsNavbar />
                </div>
              </div>
            ) : (
              <div className={styles.guestCard}>
                <div className={styles.guestIcon}>
                  <PersonIcon />
                </div>
                <div className={styles.guestMeta}>
                  <span className={styles.guestTitle}>{t('Welcome')}</span>
                  <span className={styles.guestSub}>{t('Sign in to continue')}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className={styles.headerLine} />
        </header>

        {/* Navigation */}
        <nav className={styles.drawerNav}>
          <div className={styles.navList}>
            {items.map((item, idx) => (
              <div 
                key={item.title} 
                className={styles.navItem}
                style={{ '--delay': `${idx * 0.04}s` } as React.CSSProperties}
              >
                <button
                  className={`${styles.navLink} ${selectedButton === item.value ? styles.navLinkActive : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className={styles.navLinkText}>{item.title}</span>
                  {item.listOfItems?.length > 0 && (
                    <span className={`${styles.navLinkToggle} ${expandedItem === item.value ? styles.navLinkToggleOpen : ''}`}>
                      {expandedItem === item.value ? <RemoveIcon /> : <AddIcon />}
                    </span>
                  )}
                  {!item.listOfItems?.length && (
                    <ChevronRightIcon className={styles.navLinkArrow} />
                  )}
                </button>
                
                {/* Submenu */}
                {item.listOfItems?.length > 0 && (
                  <div className={`${styles.subNav} ${expandedItem === item.value ? styles.subNavOpen : ''}`}>
                    {item.listOfItems.map((sub: any) => (
                      <button
                        key={sub.value}
                        className={styles.subNavLink}
                        onClick={() => handleSubItemPressed(sub.value, sub.to)}
                      >
                        <span className={styles.subNavDot} />
                        <span>{sub.name}</span>
                        <ChevronRightIcon className={styles.subNavArrow} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <footer className={styles.drawerFooter}>
          <div className={styles.footerLine} />
          
          {/* Quick Actions */}
          <div className={styles.quickActions}>
            <button
              className={`${styles.quickBtn} ${selectedButton === 5 ? styles.quickBtnActive : ''}`}
              onClick={() => {
                setSelectedButton(5)
                handleNavigate(NavigationEnum.PLANS_PRICES)
              }}
            >
              <LocalOfferIcon className={styles.quickBtnIcon} />
              <span>{t('Plans & Prices')}</span>
            </button>
            
            <button
              className={`${styles.accountBtn} ${selectedButton === 6 ? styles.accountBtnActive : ''}`}
              onClick={handleAccountClick}
            >
              <PersonIcon className={styles.accountBtnIcon} />
              <span>{t(isAuthenticated && profile ? 'My Account' : 'Login')}</span>
            </button>
          </div>

          {/* Language */}
          <div className={styles.langSection}>
            <div className={styles.langHeader}>
              <LanguageIcon className={styles.langIcon} />
              <span>{t('Language')}</span>
            </div>
            <div className={styles.langSelector}>
              <Language />
            </div>
          </div>

          {/* Copyright */}
          <div className={styles.copyright}>
            <span>© {new Date().getFullYear()} Layerstock</span>
          </div>
        </footer>
      </Box>
    </Drawer>
  )
}